<template>
    <b-row>
        <b-col>
            <card title="Online Order Queue">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="storeFilter" :label="`Store`" :option-label="searchLabel"
                            :prepopulate="true" :search-u-r-l="`/Store`" :params="{active: true}">
                        </SearchComponent>
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="programFilter" label="Program" :option-label="searchLabel" prepopulate
                            search-u-r-l="/Program" clearable>
                        </SearchComponent>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-table :fields="fields" :items="unlockedOnlineRefills" :sort-compare="quantitySortCompare" sort-icon-left>
                            <template #cell(prescriptions)="data">
                                <template v-for="(rx, idx) in data.item.prescriptions">
                                    <span
                                        :key="`${data.item.id}-${idx}`"
                                        :class="rx.lastShippedDate ? 'drug-hover' : ''"
                                        v-b-tooltip.hover.top
                                        :title="rx.lastShippedDate ? lastShippedTooltip(rx) : ''"
                                    >{{ rx.drugName }}</span><span v-if="idx < (data.item.prescriptions.length - 1)" :key="`sep-${data.item.id}-${idx}`">, </span>
                                </template>
                            </template>
                            <template #cell(programName)="data">
                                {{ getProgramName(data.item) }}
                            </template>
                            <template #cell(process)="data">
                                <b-button class="mx-1" variant="success" size="sm" @click="viewProcessingModal(data)">
                                    Process
                                </b-button>
                                <b-button class="mx-1" variant="danger" size="sm" @click="remove(data)"
                                    :disabled="!hasRemovePermission">
                                    Remove
                                </b-button>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
            </card>
        </b-col>

        <b-modal id="rx-modal" ref="rxModal" :title="batchModalTitle" hide-footer no-close-on-backdrop size="xl"
            @hide="onRxModalHide">
            <RxPage v-if="rxModalVisible" :prescriptionsInBatch="rxForBatch" :refillBatchMode="refillBatchMode"
                :suppressBatchNotifications="true" :card="false" @batchFinished="batchFinished">
            </RxPage>
            <hr>
            <b-button variant="dark" @click="batchCanceled">Cancel</b-button>
        </b-modal>

        <DuplicateOrderModal :visible.sync="showDuplicateModal" :open-orders="duplicateOpenOrders"
            :can-merge="true" @create-anyway="duplicateCreateAnyway" @merge="duplicateMerge" />
    </b-row>

</template>

<script lang="ts">
import { Component, Emit, Prop, Ref, Watch, Mixins } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import PatientAllergyComponent from '@/components/GSDDAllergies/GSDDAllergyComponent.vue';
import { OnlineRefillOrder, OnlineRefillPrescription } from "@/models/OnlineRefillViewModel";
import { Store } from "@/models/Store";
import { Prescription } from "@/models/Prescription";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { formatQuantity } from "@/util/quantity";
import { BModal } from "bootstrap-vue";
import { Program } from "@/models/Program";
import { LockHandler } from '@/mixins/LockHandler';
import { EntityType } from '@/models/EntityType';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import DuplicateOrderModal from '@/components/Order/DuplicateOrderModal.vue';
import OrderService from '@/services/OrderService';
import { Order } from '@/models/Order';
import { DuplicateDecision, buildDuplicateOrderQuery, findMergeableTarget } from '@/util/duplicateOrderDecision';

@Component({
    name: "PatientWebOnlineOrderQueue",
    components: {
        SearchComponent,
        PatientAllergyComponent,
        DuplicateOrderModal,
        RxPage: () => import('@/pages/Prescription/PrescriptionDetailsPage.vue')
    },
})

export default class PatientWebOnlineOrderQueue extends Mixins(LockHandler, AutoModeHotkeyMixin) {
    @Ref() readonly rxModal!: BModal;
    // Gates the embedded RxPage (PrescriptionDetailsPage): true only while the modal is open,
    // so closing destroys it and its LockHandler releases any prescription locks it still holds.
    private rxModalVisible = false;
    @Prop({ default: true, type: Boolean }) private refillBatchMode!: boolean;
    @Prop({ default: "New Refills In Batch" }) private batchModalTitle!: string;
    private unprocessedOnlineRefills: OnlineRefillOrder[] = [];
    private selectedOnlineRefill: any = {} as any;
    private storeFilter: Store = {} as Store;
    private programFilter: Program | null = null;
    private prescriptionsSelected: Prescription[] = [];
    private prescriptions: Array<Prescription> = [];
    private fields = [
        { key: 'patientName', label: 'Patient Name', sortable: true },
        { key: 'created', formatter: this.convertDate, label: 'Date', sortable: true },
        { key: 'prescriptions', label: 'Drug Name', formatter: this.drugNameFormatter, sortable: true },
        { key: 'quantityPrescribed', label: 'Qty Prescribed', formatter: this.quantityPrescribedFormatter, sortable: true },
        { key: 'quantityDispensed', label: 'Qty Dispensed', formatter: this.quantityDispensedFormatter, sortable: true },
        { key: 'programName', label: 'Program Name', sortable: true },
        { key: 'patientPickupPreference', label: 'Patient Requested', formatter: this.formatPickupPreference, sortable: true },
        { key: 'amount', label: 'Copay', formatter: this.convertAmount },
        { key: 'process', class: 'process-col', label: "" },
    ];

    formatPickupPreference(value: boolean | null | undefined): string {
        if (value === true) return 'Pickup';
        if (value === false) return 'Delivery';
        return '—';
    }

    private rxForBatch: Prescription[] = [];
    private processingOrderIds: Set<number> = new Set();
    private showDuplicateModal = false;
    private duplicateOpenOrders: Order[] = [];
    private pendingOrderUrl = "";
    // Set only while the pre-check (not the post-save 409 fallback) is awaiting a modal choice.
    private duplicateModalResolve: ((decision: DuplicateDecision | null) => void) | null = null;
    // Pre-check's outcome, carried through to batchFinished once Save and Finish creates the order.
    private pendingDuplicateDecision: DuplicateDecision | null = null;

    drugNameFormatter(items: OnlineRefillPrescription[]) {
        return items?.map(i => i.drugName).join(', ') || '';
    }

    quantityPrescribedFormatter(_value: unknown, _key: string, item: OnlineRefillOrder): string {
        return item.prescriptions?.filter(rx => rx.quantity != null).map(rx => formatQuantity(rx.quantity)).join(', ') ?? '';
    }

    quantityDispensedFormatter(_value: unknown, _key: string, item: OnlineRefillOrder): string {
        return item.prescriptions?.filter(rx => rx.dispensed != null).map(rx => formatQuantity(rx.dispensed)).join(', ') ?? '';
    }

    quantitySortValue(order: OnlineRefillOrder, field: 'quantity' | 'dispensed'): number | null {
        const values = order.prescriptions?.map(rx => rx[field]).filter((v): v is number => v != null) ?? [];
        return values.length ? Math.max(...values) : null;
    }

    quantitySortCompare(a: OnlineRefillOrder, b: OnlineRefillOrder, key: string, sortDesc: boolean): number | null {
        if (key !== 'quantityPrescribed' && key !== 'quantityDispensed') return null;
        const field = key === 'quantityPrescribed' ? 'quantity' : 'dispensed';
        const aVal = this.quantitySortValue(a, field);
        const bVal = this.quantitySortValue(b, field);
        if (aVal == null && bVal == null) return 0;
        // bootstrap-vue negates this return value when sortDesc — pre-invert so nulls stay at the same visual end either way.
        const nullIsGreater = sortDesc ? -1 : 1;
        if (aVal == null) return nullIsGreater;
        if (bVal == null) return -nullIsGreater;
        return aVal - bVal;
    }

    lastShippedTooltip(rx: OnlineRefillPrescription): string {
        if (!rx?.lastShippedDate) return 'Last shipped: never';
        const date = moment.utc(rx.lastShippedDate).format('L');
        return rx.lastShippedIsEquivalent
            ? `Last shipped: ${date} (EQV)`
            : `Last shipped: ${date}`;
    }

    get prescriptionsToShow(): Array<Prescription> {
        return this.prescriptions.map(rx => Object.assign(new Prescription(), rx));
    }

    get unlockedOnlineRefills(): OnlineRefillOrder[] {
        return this.unprocessedOnlineRefills.filter(o => !o.lockedBy);
    }

    get hasRemovePermission(): boolean {
        console.log("this.$permissions: ", this.$permissions);
        return this.$permissions.some(p => p.permissionCodeName == "Processing/Online Order Queue Remove");
    }

    async remove(selectedOrder: any) {
        try {
            const resp = await this.$bvModal.msgBoxConfirm("Are you sure you want to remove this order?",
                { title: "Remove Order", okTitle: "Remove", okVariant: "danger", noFade: true, centered: true });
            if (!resp) return;

            if (selectedOrder.item.legacyPortal) {
                await Axios.post(`/api/PatientWeb/Process/${selectedOrder.item.id}`);
            } else {
                await Axios.post(`/api/PatientWeb/Process/PatientApp/${selectedOrder.item.id}`);
            }
            this.$notification(NotificationOptions.success("Order Removed"));
            this.getUnprocessedOnlineRefills();

            await this.handleAutoModeCompletion();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    viewProcessingModal(selectedOrder: any) {
        const lockUrl = selectedOrder.item.legacyPortal
            ? `PatientWeb/${selectedOrder.item.id}/lock`
            : `PatientWeb/app/${selectedOrder.item.id}/lock`;

        // Under Auto Mode this page advances order-to-order without ever being destroyed, so
        // setExclusiveLock must reap the previous order's lock or locks would accumulate.
        this.setExclusiveLock(lockUrl, 60000)
            .then(async result => {
                // Null means the call was superseded or the acquire never landed — no lock
                // is held, so opening the processing modal would allow concurrent edits.
                if (!result) return;
                this.selectedOnlineRefill = selectedOrder;

                if (!selectedOrder.item.prescriptions?.length) {
                    this.clearAllLocks();
                    this.$notification(NotificationOptions.warning("No prescriptions to process for this order."));
                    this.markRoutedForAutoMode(selectedOrder.item.id);
                    await this.handleAutoModeCompletion();
                    return;
                }

                // For app orders, storeId is a queue-list snapshot that can diverge from the create-time regrouped store on a multi-store batch.
                const decision = await this.resolveDuplicateOpenOrders(
                    selectedOrder.item.patientId, selectedOrder.item.storeId);

                // A later call may have superseded this one while the pre-check awaited above.
                if (this.selectedOnlineRefill !== selectedOrder) return;

                if (decision === null) {
                    this.clearAllLocks();
                    this.markRoutedForAutoMode(selectedOrder.item.id);
                    await this.handleAutoModeCompletion();
                    return;
                }

                this.pendingDuplicateDecision = decision;
                this.startBatchForSelectedOrder();
            })
            .catch(error => {
                console.log(`added lock -- error == ${error}`);
                if (error.response && error.response.status == 418) {
                    this.getUnprocessedOnlineRefills();
                    this.showLockedMessage(
                        EntityType.OnlineOrder,
                        selectedOrder.item.id?.toString(),
                        selectedOrder.item.id?.toString(),
                        error.response.data
                    );
                }
            });
    }

    // Without this, Auto Mode hands the same declined/skipped order right back.
    markRoutedForAutoMode(itemId: number) {
        if (!this.$store.autoModeEnabled) return;
        const routedIds: Array<number | string> = this.$store.autoModeRoutedOnlineOrderIds ?? [];
        routedIds.push(itemId);
        this.$store.autoModeRoutedOnlineOrderIds = routedIds;
    }

    startBatchForSelectedOrder() {
        const scripts = this.selectedOnlineRefill.item.prescriptions.map((rx: any) => {
            let script = new Prescription();
            script.storeID = rx.storeId;
            script.rxNumber = rx.rxNumber;
            script.rfNumber = rx.rfNumber;
            return script;
        });
        this.openBatch(scripts);
    }

    openBatch(rxForBatch: Array<Prescription>) {
        console.log("rxForBatch: ", rxForBatch);
        if (this.refillBatchMode) {
            this.prescriptionsSelected = rxForBatch;
            this.prescriptions = rxForBatch;

            const sameStoreRx = (rx: any, rx2: any) => {
                return rx.storeID == rx2.storeID && rx.rxNumber == rx2.rxNumber;
            };

            const findMaxRfInStoreRxGroup = (rx1: Prescription, rx2: Prescription): Prescription => {
                return (rx1.rfNumber || 0) > (rx2.rfNumber ?? 0) ? rx1 : rx2;
            };

            //Find the distinct StoreId - RxNumber combinations in the selection
            const distinctStoreRxList = this.prescriptionsSelected.filter((rx: any, i: any, rxs: any) => {
                const i2 = rxs.findIndex((rx2: any) => sameStoreRx(rx, rx2));
                return i2 == i;
            }).map(rx => {
                return { storeID: rx.storeID, rxNumber: rx.rxNumber };
            });

            //Find the prescription with the max RfNumber for each distinct StoreId - RxNumber combination
            this.rxForBatch = distinctStoreRxList.map(ids => {
                const group = this.prescriptionsToShow.filter((rx: any) => sameStoreRx(rx, ids));
                return Object.assign(new Prescription(), group.reduce(findMaxRfInStoreRxGroup));
            });
        } else {
            this.rxForBatch = rxForBatch;
        }

        this.rxModalVisible = true;
        this.rxModal.show();
    }

    // Wired to @hide (not @hidden): 'hide' fires synchronously when the modal starts closing,
    // before Auto Mode advances to the next order — so the reap (and its lock-generation bump)
    // can't race the next order's lock acquisition, and the embedded RxPage is destroyed
    // (releasing its prescription locks) before a fresh one mounts for the next order.
    // Covers X/ESC, Cancel, and batch completion.
    onRxModalHide() {
        this.rxModalVisible = false;
        this.clearAllLocks();
    }

    @Emit()
    batchCanceled() {
        // hide() emits 'hide' synchronously, so onRxModalHide owns the lock reap.
        this.rxModal.hide();
    }

    @Emit("batchFinished")
    async batchFinished(...args: any[]) {
        console.log("batch Finished args:", args);

        const onlineOrderId: number | undefined = this.selectedOnlineRefill?.item?.id;
        if (onlineOrderId == null || this.processingOrderIds.has(onlineOrderId)) {
            // batchFinished can fire more than once per Save; suppress re-entry
            return;
        }
        this.processingOrderIds.add(onlineOrderId);

        let baseUrl = `order/scripts/${onlineOrderId}`;
        if (!this.selectedOnlineRefill.item.legacyPortal) {
            baseUrl = `order/scripts/app/${onlineOrderId}`;
        }

        const decision = this.pendingDuplicateDecision;
        // Held until the create succeeds so a failed create doesn't leave a stale "review it" toast.
        let demotedWarning: string | null = null;
        let freshTarget: Order | null = null;
        if (decision?.mergeTargetId != null) {
            freshTarget = await this.resolveMergeTarget(decision.mergeTargetId);
            if (!freshTarget) {
                demotedWarning = "The order you chose to merge into can no longer accept a merge (its status changed), so the new order will be filled separately — review it manually.";
                decision.mergeTargetId = null;
            }
        }

        const postUrl = buildDuplicateOrderQuery(baseUrl, decision);

        try {
            const orderResponse = await Axios.post<{ warning?: string; orderId?: number; storeId?: number }>(postUrl, this.rxForBatch);
            console.log("orderResponse: ", orderResponse);

            this.rxModal.hide();

            if (demotedWarning) {
                this.$notification(NotificationOptions.warning(demotedWarning));
            }

            if (decision?.mergeTargetId != null) {
                const newId = orderResponse.data?.orderId;
                const targetStoreId = freshTarget?.storeID;
                if (newId && targetStoreId != null && orderResponse.data?.storeId != null
                    && orderResponse.data.storeId !== targetStoreId) {
                    this.$notification(NotificationOptions.warning(
                        `Order ${newId} was created in a different store than order ${decision.mergeTargetId} and cannot be merged. Review order ${newId} — it will be filled separately.`));
                } else if (newId) {
                    // Own catch: a merge rejection must not read as "the order was not saved" — it was.
                    try {
                        await OrderService.mergeOrders(newId, decision.mergeTargetId);
                        this.$notification(NotificationOptions.success("New items merged into the existing order."));
                    } catch (mergeError) {
                        const reason = (mergeError as any)?.response?.data ?? "the merge failed";
                        this.$notification(NotificationOptions.warning(
                            `Order ${newId} was created but could not be merged into order ${decision.mergeTargetId}: ${reason}. Review order ${newId} — it will be filled separately.`));
                    }
                } else {
                    this.$notification(NotificationOptions.warning(
                        "Order was created but could not be merged automatically (no order id returned). Review it — it will be filled separately."));
                }
            } else {
                const warning: string | undefined = orderResponse.data?.warning;
                this.$notification(warning
                    ? NotificationOptions.warning(warning)
                    : NotificationOptions.success("Order created."));
            }

            await this.getUnprocessedOnlineRefills();

            await this.handleAutoModeCompletion();
        } catch (orderError) {
            console.warn(orderError);
            this.rxModal.hide();
            if (demotedWarning) {
                this.$notification(NotificationOptions.warning(demotedWarning));
            }
            if (orderError.response?.status === 409 && orderError.response.data?.requiresDuplicateConfirmation) {
                this.duplicateModalResolve = null;
                this.duplicateOpenOrders = (orderError.response.data.openOrders || []).map((o: any) => new Order(o));
                this.pendingOrderUrl = baseUrl;
                this.showDuplicateModal = true;
            } else if (orderError.response?.status === 409) {
                this.$notification(NotificationOptions.warning(orderError.response.data));
                await this.getUnprocessedOnlineRefills();
            } else {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Order", orderError));
            }
        } finally {
            this.processingOrderIds.delete(onlineOrderId);
            this.pendingDuplicateDecision = null;
        }
    }

    // The merge choice was made before RxPage opened, so re-read the target's current status at save time.
    async resolveMergeTarget(mergeTargetId: number): Promise<Order | null> {
        try {
            const openOrders = await OrderService.getOpenOrders(
                this.selectedOnlineRefill.item.patientId, this.selectedOnlineRefill.item.storeId);
            return findMergeableTarget(openOrders, mergeTargetId);
        } catch (error) {
            // Fail open: a failed re-check must not block a save the operator already committed to.
            console.warn("Merge-target re-check failed; proceeding with the original choice", error);
            return this.duplicateOpenOrders.find(o => Number(o.id) === mergeTargetId) ?? null;
        }
    }

    // Resolves whether staff may proceed: null if declined, otherwise the allow/merge choice to apply on save.
    async resolveDuplicateOpenOrders(patientId?: number, storeId?: number): Promise<DuplicateDecision | null> {
        const noOverride: DuplicateDecision = { allowDuplicate: false, mergeTargetId: null };
        if (!patientId) return noOverride;

        try {
            if (!(await OrderService.isDuplicateOpenOrderCheckEnabled())) return noOverride;

            const openOrders = await OrderService.getOpenOrders(patientId, storeId);
            if (!openOrders.length) return noOverride;

            this.duplicateOpenOrders = openOrders;
            this.showDuplicateModal = true;

            return await new Promise<DuplicateDecision | null>(resolve => {
                this.duplicateModalResolve = resolve;
            });
        } catch (error) {
            console.warn("Duplicate open-order pre-check failed; proceeding without prompt", error);
            return noOverride;
        }
    }

    @Watch('showDuplicateModal')
    onDuplicateModalVisibleChange(visible: boolean) {
        if (visible || !this.duplicateModalResolve) return;

        const resolve = this.duplicateModalResolve;
        this.duplicateModalResolve = null;
        resolve(null);
    }

    duplicateCreateAnyway() {
        if (this.duplicateModalResolve) {
            const resolve = this.duplicateModalResolve;
            this.duplicateModalResolve = null;
            resolve({ allowDuplicate: true, mergeTargetId: null });
            return;
        }
        this.retryDuplicateCreateAnyway();
    }

    duplicateMerge(existingOrder: Order) {
        const mergeTargetId = existingOrder.id ? Number(existingOrder.id) : null;
        if (this.duplicateModalResolve) {
            const resolve = this.duplicateModalResolve;
            this.duplicateModalResolve = null;
            resolve({ allowDuplicate: true, mergeTargetId });
            return;
        }
        this.retryDuplicateMerge(existingOrder);
    }

    async retryDuplicateCreateAnyway() {
        try {
            const url = buildDuplicateOrderQuery(this.pendingOrderUrl, { allowDuplicate: true, mergeTargetId: null });
            const resp = await Axios.post(url, this.rxForBatch);
            const warning: string | undefined = resp.data?.warning;
            this.$notification(warning
                ? NotificationOptions.warning(warning)
                : NotificationOptions.success("Order created."));
        } catch (error) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Order", error));
        } finally {
            await this.getUnprocessedOnlineRefills();
            await this.handleAutoModeCompletion();
        }
    }

    async retryDuplicateMerge(existingOrder: Order) {
        try {
            const url = buildDuplicateOrderQuery(this.pendingOrderUrl,
                { allowDuplicate: true, mergeTargetId: existingOrder.id ? Number(existingOrder.id) : null });
            const resp = await Axios.post<{ orderId?: number }>(url, this.rxForBatch);
            const newId = resp.data?.orderId;
            if (newId && existingOrder.id) {
                // Own catch: a merge rejection must not read as "the order was not saved" — it was.
                try {
                    await OrderService.mergeOrders(newId, Number(existingOrder.id));
                    this.$notification(NotificationOptions.success("New items merged into the existing order."));
                } catch (mergeError) {
                    const reason = (mergeError as any)?.response?.data ?? "the merge failed";
                    this.$notification(NotificationOptions.warning(
                        `Order ${newId} was created but could not be merged into order ${existingOrder.id}: ${reason}. Review order ${newId} — it will be filled separately.`));
                }
            } else {
                this.$notification(NotificationOptions.warning(
                    `Order ${newId ?? "(id unknown)"} was created but could not be merged automatically. Review it — it will be filled separately.`));
            }
        } catch (error) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Order", error));
        } finally {
            await this.getUnprocessedOnlineRefills();
            await this.handleAutoModeCompletion();
        }
    }

    async created() {
        await Axios.get(`/Store/`).then(response => {
            this.storeFilter = response.data[0];
        });

        await this.getUnprocessedOnlineRefills();

        const orderId = this.$route.params.orderId;
        if (orderId) {
            await this.loadSingleItem(parseInt(orderId));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldOrderId = oldRoute.params.orderId;
        const newOrderId = newRoute.params.orderId;

        if (oldOrderId !== newOrderId && newOrderId) {
            console.log('Route orderId parameter changed, loading new online order item');
            await this.getUnprocessedOnlineRefills();
            await this.loadSingleItem(parseInt(newOrderId));
        }
    }

    async loadSingleItem(id: number) {
        try {
            let order = this.unprocessedOnlineRefills.find(o => o.id === id);

            if (!order) {
                try {
                    const response = await Axios.get<OnlineRefillOrder>(`/PatientWeb/${id}?legacyPortal=false`);
                    order = response.data;
                } catch (patientAppError) {
                    try {
                        const response = await Axios.get<OnlineRefillOrder>(`/PatientWeb/${id}?legacyPortal=true`);
                        order = response.data;
                    } catch (legacyPortalError) {
                        throw new Error(`Order ${id} not found in either Patient App or Legacy Portal`);
                    }
                }
            }

            if (order) {
                this.viewProcessingModal({ item: order });
            }
        } catch (error) {
            console.error('Error loading single online order:', error);
            this.$notification(NotificationOptions.error('Failed to load online order'));
        }
    }

    getProgramName(item: OnlineRefillOrder) {
        return item.programName;
    }

    @Watch('storeFilter')
    @Watch('programFilter')
    getUnprocessedOnlineRefills() {
        Axios.get(`/PatientWeb/GetUnprocessed`).then(response => {
            this.unprocessedOnlineRefills = response.data;
            if (this.storeFilter) {
                this.unprocessedOnlineRefills = this.unprocessedOnlineRefills.filter((order => order.storeId == this.storeFilter.id));
            }
            if (this.programFilter) {
                console.log("programFilter =", this.programFilter);
                console.log("unprocessedOnlineRefills = ", this.unprocessedOnlineRefills);
                this.unprocessedOnlineRefills = this.unprocessedOnlineRefills.filter((order => this.getProgramName(order) == this.programFilter!.name));
            }
        }).catch(err => this.$notification(NotificationOptions.error(err)));

    }

    convertDate(date: string) {
        return moment.utc(date).format("L");
    }

    convertAmount(amt: number) {
        return '$' + amt.toFixed(2);
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }

    searchLabel(object: Store) {
        if (object?.id)
            return object.name;
        else return '';
    }
}
</script>

<style scoped>

::v-deep(.process-col) {
    width: 210px;
}

.drug-hover {
    text-decoration: underline dotted;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    cursor: help;
}

</style>
